import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IonModal, IonButton, IonContent, createAnimation } from '@ionic/react';
import styles from './MonthYearPickerModal.module.scss';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../../i18n/locale';

// Full-page, endlessly scrolling year view (like Samsung Calendar's): every year is
// a centred heading followed by its twelve months as small calendars in a 3-column
// grid, stacked vertically. More years are added above and below as you scroll.
//
// Opening is a zoom between the calendar and the year view: the calendar shrinks
// into the tile of the month it is on, and when you pick a month the year view
// zooms into that month's tile while the calendar grows out of it. Tap a month's
// name to jump to that month, or tap a day to jump straight to it — either closes
// the picker. Days with bookings are shaded with their service colour; the current
// month and year are drawn in the primary colour. A floating glass "Today" button
// brings the current year back into view, or — if it already is — takes the
// calendar to today.
//
// PERFORMANCE — why it is built the way it is. A year is ~365 day tiles plus 12
// month headers, so even a few years is ~4,000 elements. Rendered when you tap, that
// froze the screen for seconds on a phone-speed CPU, so the open animation never got
// to play. Therefore:
//   • the year view is plain elements (sections, spans, buttons), not Ionic web
//     components — building one per tile was the biggest cost;
//   • it is built once, in idle time after the calendar loads (keepContentsMounted),
//     so tapping only has to show something already built;
//   • the list is memoised, so the calendar re-rendering doesn't rebuild it;
//   • the starting years are centred on today's year, not on the month being shown,
//     so the pre-built content is the right content almost every time.

const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString(intlLocale(), { month: 'short' }),
);
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = Array.from({ length: 12 }, (_, i) => i);

const MIN_YEAR = 1970;
const MAX_YEAR = 2100;

// Years rendered around TODAY's year, and how many are added each time you scroll
// close to an end. One year is roughly 830px tall.
const INITIAL_BEFORE = 1;
const INITIAL_AFTER = 2;
const YEARS_PER_EXTENSION = 3;
const EDGE_DISTANCE_PX = 1400;

// How long after the calendar loads to wait before building the year view if the
// browser never reports an idle moment.
const PREWARM_FALLBACK_MS = 1500;

// A month's day cells in order, Sunday-first: null pads the days before the 1st.
// Rendered as one flat list into a 7-column CSS grid (no per-week rows).
const buildCells = (year: number, month: number): (number | null)[] => {
  const leading = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return [
    ...Array<null>(leading).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
};

// Same key format as the calendar's own getDateKey (YYYY-MM-DD, local, zero-padded),
// so the calendar's booked-days map can be looked up directly.
const dayKey = (year: number, month: number, day: number) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

// Ionic's stylesheet starts every modal wrapper at translateY(40px) and its default
// open animation slides it to 0. This file replaces that animation, so it has to
// put the wrapper at 0 itself — otherwise the 40px offset is left behind and the
// modal stops 40px short of the top of the screen.
const AT_REST = 'translate3d(0px, 0px, 0px) scale(1)';

// The list's own top padding (the content reserves the status-bar / camera-cutout
// inset there). A year heading should land just below that, level with the Today
// button — not at the very top edge, which on a phone is under the status bar.
const scrollTopInset = (scroller: HTMLElement) => parseFloat(getComputedStyle(scroller).paddingTop) || 0;

// Open / close is a zoom between the full calendar and one month's tile in the year
// view. The year view starts (or ends) scaled up around that tile so the tile fills
// the screen, while the calendar underneath shrinks into (or grows out of) the same
// tile; the two cross-fade. If the tile isn't on screen it falls back to a plain
// centred cross-zoom of PAGE_SCALE / YEAR_SCALE.
const PAGE_SCALE = 0.88;
const YEAR_SCALE = 1.12;
const ZOOM_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const ENTER_MS = 420;
const LEAVE_MS = 360;

// Where a month tile sits, relative to the modal wrapper's own top-left corner.
interface TileGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Wrapper (≈ viewport) size. */
  vw: number;
  vh: number;
}

const shadowRootOf = (el: HTMLElement) =>
  (el as HTMLElement & { shadowRoot?: ShadowRoot }).shadowRoot ?? el;

// ── The years, as plain elements ────────────────────────────────────────────────
// Memoised: it only re-renders when the range of years or the booked-days map
// changes, not every time the calendar page does.
interface YearListProps {
  firstYear: number;
  lastYear: number;
  dayColors?: Map<string, string[]>;
  onPickMonth: (year: number, month: number) => void;
  onPickDay: (year: number, month: number, day: number) => void;
  yearRefs: React.RefObject<Map<number, HTMLElement>>;
  monthRefs: React.RefObject<Map<string, HTMLElement>>;
}

const YearList = memo(function YearList({
  firstYear,
  lastYear,
  dayColors,
  onPickMonth,
  onPickDay,
  yearRefs,
  monthRefs,
}: YearListProps) {
  const today = new Date();
  const years = Array.from({ length: lastYear - firstYear + 1 }, (_, i) => firstYear + i);

  return (
    <>
      {years.map(year => (
        <section
          key={year}
          className={styles.yearSection}
          ref={el => {
            if (el) yearRefs.current.set(year, el);
            else yearRefs.current.delete(year);
          }}
        >
          <h2 className={`${styles.yearTitle} ${year === today.getFullYear() ? styles.yearTitleCurrent : ''}`}>
            {year}
          </h2>

          <section className={styles.monthsGrid}>
            {MONTHS.map(month => {
              const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();
              return (
                <section
                  key={month}
                  className={styles.monthCol}
                  ref={el => {
                    const key = `${year}-${month}`;
                    if (el) monthRefs.current.set(key, el);
                    else monthRefs.current.delete(key);
                  }}
                >
                  <button
                    type="button"
                    className={`${styles.monthName} ${isCurrentMonth ? styles.monthNameCurrent : ''}`}
                    aria-label={`${MONTH_NAMES[month]} ${year}`}
                    onClick={() => onPickMonth(year, month)}
                  >
                    {MONTH_NAMES[month]}
                  </button>

                  {/* The weekday letters and the days share one 7-column grid. */}
                  <section className={styles.dayGrid}>
                    {WEEKDAY_LETTERS.map((letter, i) => (
                      <span key={`w${i}`} className={styles.weekdayCell}>
                        {letter}
                      </span>
                    ))}
                    {buildCells(year, month).map((day, i) => {
                      if (day === null) {
                        return <span key={i} className={`${styles.dayTile} ${styles.dayTileEmpty}`} />;
                      }
                      const isToday = isCurrentMonth && day === today.getDate();
                      const color = dayColors?.get(dayKey(year, month, day))?.[0];
                      return (
                        <span
                          key={i}
                          className={`${styles.dayTile} ${isToday ? styles.dayTileToday : ''}`}
                          style={
                            color && !isToday
                              ? { background: `color-mix(in srgb, ${color} 45%, transparent)` }
                              : undefined
                          }
                          role="button"
                          tabIndex={0}
                          aria-label={`${MONTH_NAMES[month]} ${day}, ${year}`}
                          onClick={() => onPickDay(year, month, day)}
                        >
                          {day}
                        </span>
                      );
                    })}
                  </section>
                </section>
              );
            })}
          </section>
        </section>
      ))}
    </>
  );
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The calendar's month button — used to find the calendar page that shrinks. */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** The month the calendar is currently showing (the zoom lands on its tile). */
  currentMonth: Date;
  onSelectMonth: (year: number, month: number) => void;
  onSelectDate: (date: Date) => void;
  /** Booked days (YYYY-MM-DD) → the service colours of their bookings. */
  dayColors?: Map<string, string[]>;
}

const MonthYearPickerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  anchorRef,
  currentMonth,
  onSelectMonth,
  onSelectDate,
  dayColors,
}) => {
  const { t: tr } = useTranslation();
  // How many years are rendered before / after TODAY's year (grows as you scroll).
  const [yearsBefore, setYearsBefore] = useState(INITIAL_BEFORE);
  const [yearsAfter, setYearsAfter] = useState(INITIAL_AFTER);
  // Build the (heavy) year view in idle time after the calendar loads, so tapping
  // the month only has to show it. Until then it is built on demand when opened.
  const [prewarmed, setPrewarmed] = useState(false);

  const contentRef = useRef<HTMLIonContentElement>(null);
  const scrollElRef = useRef<HTMLElement | null>(null);
  const yearRefs = useRef<Map<number, HTMLElement>>(new Map());
  // Each month's tile, keyed "year-month": the zoom animation aims at these.
  const monthRefs = useRef<Map<string, HTMLElement>>(new Map());
  // The month being zoomed into when the picker closes (set when you pick one).
  const leaveTarget = useRef<{ year: number; month: number } | null>(null);
  // Scroll position captured just before years are added at the top, so the list
  // can be put back where it was once the new years push everything down.
  const pendingPrepend = useRef<{ height: number; top: number } | null>(null);
  // The latest callbacks, so the memoised list can keep stable handlers.
  const latest = useRef({ onSelectMonth, onSelectDate, onClose });

  useEffect(() => {
    latest.current = { onSelectMonth, onSelectDate, onClose };
  });

  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setPrewarmed(true), { timeout: 4000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setPrewarmed(true), PREWARM_FALLBACK_MS);
    return () => window.clearTimeout(id);
  }, []);

  const shownYear = currentMonth.getFullYear();
  const shownMonth = currentMonth.getMonth();
  const today = new Date();
  const thisYear = today.getFullYear();

  // Years rendered: around today's year, widened if the calendar is showing a year
  // outside that window.
  const firstYear = Math.max(MIN_YEAR, Math.min(thisYear - yearsBefore, shownYear - 1));
  const lastYear = Math.min(MAX_YEAR, Math.max(thisYear + yearsAfter, shownYear + 1));

  // Scroll the list so a year's heading sits at the top, computed against the list's
  // own scroll container and applied directly (element.scrollIntoView() proved
  // unreliable here).
  const scrollToSection = (section: HTMLElement, behavior: ScrollBehavior = 'auto') => {
    const el = scrollElRef.current;
    if (!el) return;
    const delta = section.getBoundingClientRect().top - el.getBoundingClientRect().top - scrollTopInset(el);
    el.scrollTo({ top: el.scrollTop + delta, behavior });
  };

  // Years were just added above: the content grew taller, so move the scroll
  // position down by exactly that much — the visible months stay where they were.
  useLayoutEffect(() => {
    const pending = pendingPrepend.current;
    const el = scrollElRef.current;
    if (!pending || !el) return;
    el.scrollTop = pending.top + (el.scrollHeight - pending.height);
    pendingPrepend.current = null;
  }, [yearsBefore]);

  const handleScroll = () => {
    const el = scrollElRef.current;
    if (!el || pendingPrepend.current) return;
    const nearTop = el.scrollTop < EDGE_DISTANCE_PX;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < EDGE_DISTANCE_PX;

    if (nearTop && firstYear > MIN_YEAR) {
      pendingPrepend.current = { height: el.scrollHeight, top: el.scrollTop };
      setYearsBefore(n => n + YEARS_PER_EXTENSION);
    } else if (nearBottom && lastYear < MAX_YEAR) {
      setYearsAfter(n => n + YEARS_PER_EXTENSION);
    }
  };

  // The calendar's own scrolling content (a sibling of this modal inside the page).
  // Ionic React's <IonPage> renders a div with the class `ion-page`, not an <ion-page>.
  // Animated alongside the modal so it is the thing that shrinks. The header and this
  // modal are left alone — the modal is a child of the page, so transforming the page
  // itself would scale the modal too.
  const calendarContent = (): HTMLElement | null =>
    (anchorRef.current?.closest('.ion-page')?.querySelector(':scope > ion-content') as HTMLElement | null) ?? null;

  // A month tile's position and size, measured against the modal wrapper — or null
  // if that month isn't rendered or isn't on screen (then the zoom falls back to
  // the centred one).
  const tileGeometry = (baseEl: HTMLElement, year: number, month: number): TileGeometry | null => {
    const wrapper = shadowRootOf(baseEl).querySelector('.modal-wrapper') as HTMLElement | null;
    const tile = monthRefs.current.get(`${year}-${month}`);
    if (!wrapper || !tile) return null;
    const w = wrapper.getBoundingClientRect();
    const t = tile.getBoundingClientRect();
    const geometry = { x: t.left - w.left, y: t.top - w.top, w: t.width, h: t.height, vw: w.width, vh: w.height };
    const onScreen =
      geometry.w > 0 &&
      geometry.h > 0 &&
      geometry.y + geometry.h > 0 &&
      geometry.y < geometry.vh &&
      geometry.x + geometry.w > 0 &&
      geometry.x < geometry.vw;
    return onScreen ? geometry : null;
  };

  // The year view scaled up around the tile's centre so the tile fills the screen's
  // width, and moved so that centre sits at the screen's centre.
  const yearZoomedIn = (g: TileGeometry) => {
    const cx = g.x + g.w / 2;
    const cy = g.y + g.h / 2;
    return {
      origin: `${cx}px ${cy}px`,
      transform: `translate3d(${g.vw / 2 - cx}px, ${g.vh / 2 - cy}px, 0px) scale(${g.vw / g.w})`,
    };
  };

  // The calendar scaled down (about its own top-left) until it fits inside the tile.
  const calendarShrunk = (g: TileGeometry, content: HTMLElement) => {
    const r = content.getBoundingClientRect();
    // Fit inside the tile (the calendar is taller and narrower than a month tile).
    const scale = Math.min(g.w / r.width, g.h / r.height);
    const tx = g.x + g.w / 2 - r.left - (r.width * scale) / 2;
    const ty = g.y + g.h / 2 - r.top - (r.height * scale) / 2;
    return `translate3d(${tx}px, ${ty}px, 0px) scale(${scale})`;
  };

  // Drop any animation still holding the calendar hidden, so it can be measured at
  // its real size.
  const resetCalendar = (content: HTMLElement | null) => {
    content?.getAnimations?.().forEach(animation => animation.cancel());
  };

  const enterAnimation = (baseEl: HTMLElement) => {
    const root = shadowRootOf(baseEl);
    const wrapperEl = root.querySelector('.modal-wrapper')!;
    const content = calendarContent();
    resetCalendar(content);

    // Scroll the list to the year being shown *before* measuring, so the month's
    // tile is already where it will end up when the animation lands.
    const scroller = (baseEl.querySelector('ion-content') as HTMLElement | null)?.shadowRoot?.querySelector(
      '.inner-scroll',
    ) as HTMLElement | null;
    const yearSection = yearRefs.current.get(shownYear);
    if (scroller && yearSection) {
      scroller.scrollTop += yearSection.getBoundingClientRect().top - scroller.getBoundingClientRect().top - scrollTopInset(scroller);
    }

    const g = tileGeometry(baseEl, shownYear, shownMonth);
    const animations = [];

    if (g && content) {
      const zoom = yearZoomedIn(g);
      animations.push(
        createAnimation()
          .addElement(wrapperEl)
          .beforeStyles({ 'transform-origin': zoom.origin })
          .afterClearStyles(['transform-origin'])
          .keyframes([
            { offset: 0, opacity: '0', transform: zoom.transform },
            { offset: 1, opacity: '1', transform: AT_REST },
          ]),
        createAnimation()
          .addElement(content)
          .beforeStyles({ 'transform-origin': '0px 0px' })
          .afterClearStyles(['opacity', 'transform', 'transform-origin'])
          .keyframes([
            { offset: 0, opacity: '1', transform: 'translate3d(0px, 0px, 0px) scale(1)' },
            { offset: 1, opacity: '0', transform: calendarShrunk(g, content) },
          ]),
      );
    } else {
      animations.push(
        createAnimation()
          .addElement(wrapperEl)
          .keyframes([
            { offset: 0, opacity: '0', transform: `translate3d(0px, 0px, 0px) scale(${YEAR_SCALE})` },
            { offset: 1, opacity: '1', transform: AT_REST },
          ]),
      );
      if (content) {
        animations.push(
          createAnimation()
            .addElement(content)
            .afterClearStyles(['opacity', 'transform'])
            .keyframes([
              { offset: 0, opacity: '1', transform: 'scale(1)' },
              { offset: 1, opacity: '0', transform: `scale(${PAGE_SCALE})` },
            ]),
        );
      }
    }

    return createAnimation()
      .addElement(baseEl)
      .easing(ZOOM_EASING)
      .duration(ENTER_MS)
      .addAnimation(animations);
  };

  const leaveAnimation = (baseEl: HTMLElement) => {
    const root = shadowRootOf(baseEl);
    const wrapperEl = root.querySelector('.modal-wrapper')!;
    const content = calendarContent();
    resetCalendar(content);

    // Zoom into the month that was picked (or, for any other way of closing, the
    // month the calendar is on).
    const target = leaveTarget.current ?? { year: shownYear, month: shownMonth };
    const g = tileGeometry(baseEl, target.year, target.month);
    const animations = [];

    if (g && content) {
      const zoom = yearZoomedIn(g);
      animations.push(
        createAnimation()
          .addElement(wrapperEl)
          .beforeStyles({ 'transform-origin': zoom.origin })
          .afterClearStyles(['transform-origin'])
          .keyframes([
            { offset: 0, opacity: '1', transform: AT_REST },
            { offset: 1, opacity: '0', transform: zoom.transform },
          ]),
        createAnimation()
          .addElement(content)
          .beforeStyles({ 'transform-origin': '0px 0px' })
          .afterClearStyles(['opacity', 'transform', 'transform-origin'])
          .keyframes([
            { offset: 0, opacity: '0', transform: calendarShrunk(g, content) },
            { offset: 1, opacity: '1', transform: 'translate3d(0px, 0px, 0px) scale(1)' },
          ]),
      );
    } else {
      animations.push(
        createAnimation()
          .addElement(wrapperEl)
          .keyframes([
            { offset: 0, opacity: '1', transform: AT_REST },
            { offset: 1, opacity: '0', transform: `translate3d(0px, 0px, 0px) scale(${YEAR_SCALE})` },
          ]),
      );
      if (content) {
        animations.push(
          createAnimation()
            .addElement(content)
            .afterClearStyles(['opacity', 'transform'])
            .keyframes([
              { offset: 0, opacity: '0', transform: `scale(${PAGE_SCALE})` },
              { offset: 1, opacity: '1', transform: 'scale(1)' },
            ]),
        );
      }
    }

    return createAnimation()
      .addElement(baseEl)
      .easing(ZOOM_EASING)
      .duration(LEAVE_MS)
      .addAnimation(animations);
  };

  // Once the modal is on screen, grab its scroll container (the list was already
  // scrolled to the shown year before the animation, in enterAnimation).
  const handlePresented = async () => {
    scrollElRef.current = (await contentRef.current?.getScrollElement()) ?? null;
    const section = yearRefs.current.get(shownYear);
    if (section) scrollToSection(section);
  };

  const handleDismissed = () => {
    // Safety net: the calendar underneath is hidden by the open animation and put
    // back by the close animation. If the picker was ever dismissed without that
    // animation running, drop any animation still holding it hidden.
    resetCalendar(calendarContent());
    scrollElRef.current = null;
    pendingPrepend.current = null;
    leaveTarget.current = null;
    setYearsBefore(INITIAL_BEFORE);
    setYearsAfter(INITIAL_AFTER);
    onClose();
  };

  // Today button. Scrolled away from the current year: bring that year into view.
  // Already on the current year: take the calendar to today (which closes this).
  // (Today's year is always part of the rendered list.)
  const handleToday = () => {
    const el = scrollElRef.current;
    const section = yearRefs.current.get(thisYear);
    if (!el || !section) return;

    // Which year is "in view"? The one under a line a third of the way down.
    const box = el.getBoundingClientRect();
    const probe = box.top + box.height * 0.35;
    const rect = section.getBoundingClientRect();
    const onCurrentYear = rect.top <= probe && rect.bottom >= probe;

    if (onCurrentYear) {
      leaveTarget.current = { year: thisYear, month: today.getMonth() };
      onSelectDate(today);
      onClose();
    } else {
      scrollToSection(section, 'smooth');
    }
  };

  // Stable handlers for the memoised list (they read the latest props via a ref).
  const pickMonth = useCallback((year: number, month: number) => {
    leaveTarget.current = { year, month };
    latest.current.onSelectMonth(year, month);
    latest.current.onClose();
  }, []);

  const pickDay = useCallback((year: number, month: number, day: number) => {
    leaveTarget.current = { year, month };
    latest.current.onSelectDate(new Date(year, month, day));
    latest.current.onClose();
  }, []);

  return (
    <IonModal
      isOpen={isOpen}
      keepContentsMounted={true}
      onDidDismiss={handleDismissed}
      onDidPresent={handlePresented}
      enterAnimation={enterAnimation}
      leaveAnimation={leaveAnimation}
      className={styles.monthPickerModal}
    >
      {(isOpen || prewarmed) && (
        <IonContent
          ref={contentRef}
          className={styles.content}
          scrollEvents={true}
          onIonScroll={handleScroll}
        >
          {/* Floating glass button, pinned top-left over the scrolling years. */}
          <IonButton
            slot="fixed"
            fill="clear"
            className={styles.todayButton}
            aria-label={tr('pages.bookingCalendar.MonthYearPickerModal.today')}
            onClick={handleToday}
          >{tr('pages.bookingCalendar.MonthYearPickerModal.today')}
          </IonButton>

          <YearList
            firstYear={firstYear}
            lastYear={lastYear}
            dayColors={dayColors}
            onPickMonth={pickMonth}
            onPickDay={pickDay}
            yearRefs={yearRefs}
            monthRefs={monthRefs}
          />
        </IonContent>
      )}
    </IonModal>
  );
};

export default MonthYearPickerModal;
